import React from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'
import { observer } from 'mobx-react'
import { ExpandIcon, ContractIcon } from './Icons'
import { formatCompactNumber } from '../../../utils'
const PYRAMID_ANIMATION_MS = 450

const dummyData = [
  { AgeGroup: '80plus', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '75_79', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '70_74', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '65_69', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '60_64', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '55_59', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '50_54', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '45_49', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '40_44', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '35_39', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '30_34', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '25_29', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '20_24', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '15_19', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '10_14', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '5_9', MalePop: 0, FemalePop: 0, TotalPop: 0 },
  { AgeGroup: '0_4', MalePop: 0, FemalePop: 0, TotalPop: 0 }
]

const ageOrder = [
  '80plus', '75_79', '70_74', '65_69', '60_64', '55_59', '50_54', '45_49',
  '40_44', '35_39', '30_34', '25_29', '20_24', '15_19', '10_14', '5_9', '0_4'
]
const sortPyramidsByAge = (rows) => {
  return [...rows].sort((a, b) => {
    let indexA = ageOrder.indexOf(a.AgeGroup)
    let indexB = ageOrder.indexOf(b.AgeGroup)
    if (indexA === -1) indexA = 100
    if (indexB === -1) indexB = 100
    return indexA - indexB
  })
}

const matchesDs = (item, ds) => {
  if (!item) return false
  if (!ds) return true
  const targetDs = ds.toLowerCase()
  const itemType = (item.type || item.ds_name || '').toLowerCase()
  return itemType === targetDs
}

const resolvePyramidsFromYearKeyedDatas = (pd, yearStr, datasetType) => {
  if (!pd) {
    return { pyramids: null, yearStr }
  }
  if (Array.isArray(pd)) {
    if (pd.length > 0 && pd[0].AgeGroup) {
      return { pyramids: pd, yearStr }
    }
    const target = datasetType
      ? pd.find(item => matchesDs(item, datasetType))
      : pd[0]
    if (target && target.Pyramids && target.Pyramids.length > 0) {
      return { pyramids: target.Pyramids, yearStr }
    }
    if (pd[0] && pd[0].Pyramids && pd[0].Pyramids.length > 0) {
      return { pyramids: pd[0].Pyramids, yearStr }
    }
    return { pyramids: null, yearStr }
  }

  if (typeof pd !== 'object' || Object.keys(pd).length === 0) {
    return { pyramids: null, yearStr }
  }

  let activeYear = yearStr
  if (!activeYear || !pd[activeYear] || (Array.isArray(pd[activeYear]) && pd[activeYear].length === 0)) {
    const keys = Object.keys(pd)
    activeYear = keys.find((k) => {
      const raw = pd[k]
      const d = Array.isArray(raw) ? raw : (raw ? [raw] : [])
      if (!d || d.length === 0) return false
      const targetDataset = datasetType ? (d.find(item => matchesDs(item, datasetType)) || d[0]) : d[0]
      return targetDataset && targetDataset.Pyramids && targetDataset.Pyramids.length > 0
    }) || keys[0] || null
  }

  if (activeYear && pd[activeYear]) {
    const raw = pd[activeYear]
    const yearData = Array.isArray(raw) ? raw : (raw ? [raw] : [])
    if (yearData && yearData.length > 0) {
      const targetDataset = datasetType ? (yearData.find(d => matchesDs(d, datasetType)) || yearData[0]) : yearData[0]
      if (targetDataset && targetDataset.Pyramids && targetDataset.Pyramids.length > 0) {
        return { pyramids: targetDataset.Pyramids, yearStr: activeYear }
      }
    }
  }
  return { pyramids: null, yearStr: activeYear }
}

const TooltipMale = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>Age: {label}</p>
        <p style={{ margin: 0, color: '#5EB1BC' }}>Male: {formatCompactNumber(payload[0].value)}</p>
      </div>
    )
  }
  return null
}

const TooltipFemale = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>Age: {label}</p>
        <p style={{ margin: 0, color: '#EC4899' }}>Female: {formatCompactNumber(payload[0].value)}</p>
      </div>
    )
  }
  return null
}

const CenterTick = (props) => {
  const { y, payload } = props
  return (
    <text x='50%' y={y} dy={4} textAnchor='middle' fill='#6B7280' fontSize={11}>
      {payload.value}
    </text>
  )
}

const resolvePyramidViewModel = ({
  polygonData,
  singlePolygonData,
  selectionStore,
  rightSidebarStore,
  selectedYearStr,
  populationDataset
}) => {
  const selectedItem = selectionStore && selectionStore.item
  const selectedLineId = rightSidebarStore && rightSidebarStore.selectedLine
  const showIndividualLines = rightSidebarStore && rightSidebarStore.showIndividualPolygonLines
  const fetchedLinePopulationDatas = showIndividualLines && rightSidebarStore.selectedLinePopulationDatas
  const selectedPolygon = selectedLineId != null && polygonData && polygonData.length > 0
    ? polygonData.find(p => String(p.id) === String(selectedLineId))
    : null

  let sourcePyramids = null

  if (selectedLineId != null) {
    let pd = fetchedLinePopulationDatas
    const singlePolygonId = rightSidebarStore && rightSidebarStore.singlePolygonId
    if (!pd && singlePolygonData && singlePolygonData.population_datas &&
      singlePolygonId != null && String(singlePolygonId) === String(selectedLineId)) {
      pd = singlePolygonData.population_datas
    }
    if (!pd && selectedPolygon && selectedPolygon.population_datas) {
      pd = selectedPolygon.population_datas
    }
    const resolved = resolvePyramidsFromYearKeyedDatas(pd, selectedYearStr, populationDataset)
    if (resolved.pyramids) sourcePyramids = resolved.pyramids
  }

  if (!sourcePyramids && selectedLineId == null && selectedItem && selectedItem.population_datas) {
    const resolved = resolvePyramidsFromYearKeyedDatas(selectedItem.population_datas, selectedYearStr, populationDataset)
    if (resolved.pyramids) sourcePyramids = resolved.pyramids
  }

  if (!sourcePyramids && selectedLineId == null && singlePolygonData && singlePolygonData.population_datas) {
    const resolved = resolvePyramidsFromYearKeyedDatas(singlePolygonData.population_datas, selectedYearStr, populationDataset)
    if (resolved.pyramids) sourcePyramids = resolved.pyramids
  }

  if (!sourcePyramids && selectedLineId == null && selectedItem && selectedItem.analysis) {
    const selectedAnalysis = selectedItem.analysis
    if (selectedAnalysis && selectedAnalysis.pyramids && selectedAnalysis.pyramids.total && selectedAnalysis.pyramids.total.length > 0) {
      const pyramidsTotal = selectedAnalysis.pyramids.total
      const pyramidsFemale = selectedAnalysis.pyramids.female
      const pyramidsMale = selectedAnalysis.pyramids.male
      sourcePyramids = pyramidsTotal.map((t, i) => {
        const femaleValue = pyramidsFemale && pyramidsFemale[i] ? pyramidsFemale[i].value : 0
        const maleValue = pyramidsMale && pyramidsMale[i] ? pyramidsMale[i].value : 0
        return {
          AgeGroup: t.age,
          TotalPop: t.value || 0,
          FemalePop: femaleValue || 0,
          MalePop: maleValue || 0
        }
      })
    }
  }

  const isIndividualShapeOrBoundarySelected = (selectedItem && (selectedItem.id != null || selectedItem.inner_id != null || selectedItem.remote_db_id != null)) || (singlePolygonData != null)
  if (!sourcePyramids && selectedLineId == null && !isIndividualShapeOrBoundarySelected && polygonData && polygonData.length > 0) {
    const pyramidMap = {}
    let polyYearStr = selectedYearStr
    if (!polyYearStr && polygonData[0] && polygonData[0].population_datas) {
      const pd0 = polygonData[0].population_datas
      if (typeof pd0 === 'object' && !Array.isArray(pd0)) {
        const keys = Object.keys(pd0)
        polyYearStr = keys.find((k) => {
          const d = pd0[k]
          if (!d || d.length === 0) return false
          const targetDataset = populationDataset ? d.find(item => matchesDs(item, populationDataset)) : d[0]
          return targetDataset && targetDataset.Pyramids && targetDataset.Pyramids.length > 0
        }) || keys[0] || null
      }
    }
    polygonData.forEach((poly) => {
      let pyramids = null
      const pd = poly.population_datas
      if (pd && typeof pd === 'object' && !Array.isArray(pd) && polyYearStr && pd[polyYearStr]) {
        const yd = pd[polyYearStr]
        if (yd && yd.length > 0) {
          const targetDataset = populationDataset ? yd.find(d => d.type === populationDataset) : yd[0]
          pyramids = targetDataset && targetDataset.Pyramids ? targetDataset.Pyramids : null
        }
      } else if (pd && Array.isArray(pd) && pd.length > 0) {
        const targetDataset = populationDataset ? pd.find(d => d.type === populationDataset) : pd[0]
        pyramids = targetDataset && targetDataset.Pyramids ? targetDataset.Pyramids : null
      }
      if (pyramids) {
        pyramids.forEach((p) => {
          if (!pyramidMap[p.AgeGroup]) {
            pyramidMap[p.AgeGroup] = { AgeGroup: p.AgeGroup, MalePop: 0, FemalePop: 0, TotalPop: 0 }
          }
          pyramidMap[p.AgeGroup].MalePop += p.MalePop
          pyramidMap[p.AgeGroup].FemalePop += p.FemalePop
          pyramidMap[p.AgeGroup].TotalPop += p.TotalPop
        })
      }
    })
    const aggregated = Object.values(pyramidMap)
    if (aggregated.length > 0) sourcePyramids = aggregated
  }

  const rawData = sortPyramidsByAge(sourcePyramids || dummyData)

  return {
    selectedPolygonName: selectedPolygon && selectedPolygon.name ? selectedPolygon.name : null,
    rawData
  }
}

const PopulationPyramid = observer(({
  polygonData,
  singlePolygonData,
  uiStore,
  selectionStore,
  rightSidebarStore,
  visibleSeries,
  onToggleSeries,
  isExpanded,
  onToggleExpand
}) => {
  const storeYear = rightSidebarStore && (
    rightSidebarStore.populationSelectedYear || rightSidebarStore.latestYear
  )
  const selectedYearStr = storeYear ? String(storeYear) : null
  const populationDataset = rightSidebarStore && rightSidebarStore.populationDataset

  const chartScopeKey = [
    rightSidebarStore && rightSidebarStore.targetGroupId,
    rightSidebarStore && rightSidebarStore.selectedLine,
    rightSidebarStore && rightSidebarStore.singlePolygonId,
    rightSidebarStore && rightSidebarStore.showIndividualPolygonLines,
    populationDataset,
    selectedYearStr
  ].join('-')

  const { selectedPolygonName, rawData } = resolvePyramidViewModel({
    polygonData,
    singlePolygonData,
    selectionStore,
    rightSidebarStore,
    selectedYearStr,
    populationDataset
  })

  const formattedData = rawData.map(item => ({
    AgeGroup: item.AgeGroup.replace('plus', '+').replace('_', '-'),
    Male: visibleSeries && visibleSeries.male ? Math.abs(item.MalePop) : 0,
    Female: visibleSeries && visibleSeries.female ? Math.abs(item.FemalePop) : 0
  }))
  const yearMaxPop = formattedData.length
    ? Math.max(...formattedData.map(d => Math.max(d.Male, d.Female)), 0)
    : 0
  const peakPop = yearMaxPop
  const domainMax = peakPop > 0 ? Math.ceil(peakPop) : 100
  const chartDomain = [0, domainMax]
  const expandedInnerHeight = Math.min(560, Math.max(340, (formattedData.length || 0) * 22 + 80))

  const barAnimationId = `${chartScopeKey}-${selectedYearStr || 'year'}`

  return (
    <div className={`people-data-sidebar__section people-data-sidebar__section--pyramid ${isExpanded ? 'people-data-sidebar__section--expanded-chart' : ''}`}>
      <div className='people-data-sidebar__section-header'>
        <div>
          <h3 className='people-data-sidebar__section-title'>Age Distribution</h3>
          {selectedPolygonName && (
            <div className='people-data-sidebar__section-subtitle'>{selectedPolygonName}</div>
          )}
        </div>
        <button
          type='button'
          className='people-data-sidebar__expand-btn'
          onClick={onToggleExpand}
          aria-label={isExpanded ? 'Contract age distribution chart' : 'Expand age distribution chart'}
        >
          {isExpanded ? <ContractIcon /> : <ExpandIcon />}
        </button>
      </div>
      {rightSidebarStore && rightSidebarStore.pyramidLoading
        ? (
          <div
            className='people-data-sidebar__pyramid-chart-container'
            style={{
              display: 'flex',
              width: '100%',
              height: isExpanded ? '100%' : 350,
              marginTop: isExpanded ? '8px' : '20px',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div className='people-data-sidebar__loading people-data-sidebar__loading--spinner loading loading-dark' />
          </div>
          )
        : (
          <div
            className='people-data-sidebar__pyramid-chart-container'
            style={{
              display: 'flex',
              width: '100%',
              height: isExpanded ? '100%' : 350,
              marginTop: isExpanded ? '8px' : '20px',
              alignItems: 'center'
            }}
          >
            <div style={{ display: 'flex', width: '100%', height: isExpanded ? expandedInnerHeight : 350 }}>
              <ResponsiveContainer key={`male-${chartScopeKey}`} width='42%' height='100%'>
                <BarChart layout='vertical' data={formattedData} margin={{ top: 20, right: 0, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray='3 3' horizontal={false} vertical />
                  <XAxis type='number' reversed domain={chartDomain} stroke='#6B7280' fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis dataKey='AgeGroup' type='category' hide />
                  <Tooltip content={<TooltipMale />} cursor={{ fill: '#f3f4f6' }} />
                  <Bar
                    dataKey='Male'
                    fill='#5EB1BC'
                    isAnimationActive
                    animationDuration={PYRAMID_ANIMATION_MS}
                    animationEasing='ease-out'
                    animationId={barAnimationId}
                  />
                </BarChart>
              </ResponsiveContainer>
              <ResponsiveContainer key={`center-${chartScopeKey}`} width='16%' height='100%'>
                <BarChart layout='vertical' data={formattedData} margin={{ top: 20, right: 0, left: 0, bottom: 20 }}>
                  <XAxis type='number' fontSize={11} tick={false} tickLine={false} axisLine={false} />
                  <YAxis
                    dataKey='AgeGroup'
                    type='category'
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    tick={<CenterTick />}
                    width={60}
                  />
                  <Bar
                    dataKey='Male'
                    fill='transparent'
                    isAnimationActive
                    animationDuration={PYRAMID_ANIMATION_MS}
                    animationEasing='ease-out'
                    animationId={barAnimationId}
                  />
                </BarChart>
              </ResponsiveContainer>
              <ResponsiveContainer key={`female-${chartScopeKey}`} width='42%' height='100%'>
                <BarChart layout='vertical' data={formattedData} margin={{ top: 20, right: 10, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray='3 3' horizontal={false} vertical />
                  <XAxis type='number' domain={chartDomain} stroke='#6B7280' fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis dataKey='AgeGroup' type='category' hide />
                  <Tooltip content={<TooltipFemale />} cursor={{ fill: '#f3f4f6' }} />
                  <Bar
                    dataKey='Female'
                    fill='#EC4899'
                    isAnimationActive
                    animationDuration={PYRAMID_ANIMATION_MS}
                    animationEasing='ease-out'
                    animationId={barAnimationId}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          )}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px', fontSize: '12px', color: '#6B7280' }}>
        <button
          type='button'
          onClick={() => onToggleSeries('male')}
          aria-label={`${visibleSeries && visibleSeries.male ? 'Hide' : 'Show'} male series`}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginRight: '20px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: '#6B7280',
            opacity: visibleSeries && visibleSeries.male ? 1 : 0.45,
            padding: 0,
            fontSize: '12px',
            lineHeight: '1',
            fontFamily: 'inherit'
          }}
        >
          <div style={{ width: '12px', height: '12px', backgroundColor: '#5EB1BC', marginRight: '5px' }} />
          <span style={{ textDecoration: visibleSeries && visibleSeries.male ? 'none' : 'line-through' }}>Male</span>
        </button>
        <button
          type='button'
          onClick={() => onToggleSeries('female')}
          aria-label={`${visibleSeries && visibleSeries.female ? 'Hide' : 'Show'} female series`}
          style={{
            display: 'flex',
            alignItems: 'center',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: '#6B7280',
            opacity: visibleSeries && visibleSeries.female ? 1 : 0.45,
            padding: 0,
            fontSize: '12px',
            lineHeight: '1',
            fontFamily: 'inherit'
          }}
        >
          <div style={{ width: '12px', height: '12px', backgroundColor: '#EC4899', marginRight: '5px' }} />
          <span style={{ textDecoration: visibleSeries && visibleSeries.female ? 'none' : 'line-through' }}>Female</span>
        </button>
      </div>
    </div>
  )
})

export default PopulationPyramid
